
'use client';

import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Mousewheel, Keyboard } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import {
  ArrowUpIcon, ArrowDownIcon, SpeakerWaveIcon, SpeakerXMarkIcon,
} from '@heroicons/react/16/solid';
import {
  Avatar, Button, LinkStyled, Modal,
} from '@components/common';
import { useTranslation } from 'next-i18next';
import { useSwrStoriesFetchAll } from '@hooks/useSwrApi/endpoints/stories/useSwrStoriesFetchAll';
import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  PauseIcon, PhoneIcon, PlayIcon, ShareIcon,
} from '@heroicons/react/20/solid';
import { useSwrStoriesLike } from '@hooks/useSwrApi/endpoints/stories/useSwrStoriesLike';
import { useSwrStoriesDeleteLike } from '@hooks/useSwrApi/endpoints/stories/useSwrStoriesDeleteLike';
import Confetti from 'react-confetti-boom';
import { CustomConfirmModalContent } from '@components/templates/cv/partials/modals/CustomConfirmModalContent';
import { useAuthClient } from '@root/src/hooks';
import { StoriesClapIcon, StoriesUnClapIcon } from '@components/common/Icons';
import { useSwrResumeCheckUnlock } from '@hooks/useSwrApi/endpoints/resume-operations';
import { showAddCompanyInfoModal } from '@components/features/modals/employer/AddCompanyInfoModal';
import { CvDetailHub } from '@components/templates/cv/CvDetailHub';
import { showUpgradeSubscriptionModal } from '@components/features/modals/employer/subscription/UpgradeSubscriptionModal';
import { ShareModal } from '@components/features/modals/share-modal/ShareModal';
import { getI18nLinkHref } from '@services/i18nPaths';
import { trackEvent } from '@services/tracking';
import { useAppSelector } from '@store/index';
import { getUserHeaderInfo } from '@store/reducers/userSelector';
import { useSwrStoriesDeleteDislike } from '@hooks/useSwrApi/endpoints/stories/useSwrStoriesDeleteDislike';
import { useSwrStoriesDislike } from '@hooks/useSwrApi/endpoints/stories/useSwrStoriesDislike';
import { FlagIcon as FlagSolid } from '@heroicons/react/24/solid';
import { FlagIcon as FlagOutline } from '@heroicons/react/24/outline';
import { StoriesActionIcons } from '@components/templates/stories/StoriesActionIcons';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';
import sendGoogleTagEvent from '@hooks/useSendGoogleTagEvent';
import { useResumeAccessCheck } from '@components/templates/stories/hooks/useResumeAccesCheck';


interface SwiperStoriesCardsProps {
  initialData: ApiSchema<'ResumeStoriesResult'>;
  locale: Locale;
  ps?: string;
}

export default function SwiperStoriesCards({ initialData, locale, ps }: SwiperStoriesCardsProps) {
  const { t } = useTranslation('common');
  const headerInfoData = useAppSelector(getUserHeaderInfo);
  const userId = headerInfoData?.userId;
  const videoRefs = useRef<HTMLVideoElement[]>([]);
  const [swiperIndex, setSwiperIndex] = useState(0);
  const [showConfetti, setShowConfetti] = useState(false);
  const [isOpenShareModal, toggleShareModal] = useState(false);
  const { isAuth } = useAuthClient();
  const { withLocale } = useExtendedRouter();

  const [confirmModal, setConfirmModal] = useState<boolean>(false);
  const [selectedCvSlug, setSelectedCvSlug] = useState<string | null>(null);
  const [muted, setMuted] = useState(true);
  const [hasInteracted, setHasinteracted] = useState(false);
  const [showIcon, setShowIcon] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const iconTimeout = useRef<NodeJS.Timeout | null>(null);
  const progressRefs = useRef<HTMLDivElement[]>([]);


  const {
    data, size, setSize, isValidating,
  } = useSwrStoriesFetchAll({
    locale,
    prioritisedSlug: ps,
    serverSideData: initialData,
  });


  const totalPages = initialData?.pages ?? Infinity;
  const items = useMemo(
    () => data?.flatMap((page) => page.items || []) || [],
    [data],
  );
  const [activeVideoSlug, setActiveVideoSlug] = useState(items[0].slug);
  const hasTrackedInitialView = useRef(false);

  const { isChecking: isLoadingSecurityCheck, isAllowed: isAllowedPlayingVideo, check: checkResumeAccess } = useResumeAccessCheck(locale, activeVideoSlug);

  useEffect(() => {
    if (!activeVideoSlug) return;

    void checkResumeAccess(activeVideoSlug);
    videoRefs.current.forEach((v, i) => {
      if (!v) return;
      if (i === swiperIndex) v.play().catch(() => {});
      else {
        v.pause();
        v.currentTime = 0;
      }
    });
  }, [activeVideoSlug, checkResumeAccess, swiperIndex]);


  // Track initial story view when stories are first loaded
  useEffect(() => {
    if (items && items.length > 0 && !hasTrackedInitialView.current) {
      sendGoogleTagEvent('viewed_interview', 'Custom');
      hasTrackedInitialView.current = true;
    }
  }, [items]);

  const { trigger: triggerLike } = useSwrStoriesLike({
    slug: activeVideoSlug ?? '',
    successCallback: () => {
      items[swiperIndex].likedByMe = true;
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 4000);
    },
  });

  const { trigger: triggerDeleteLike } = useSwrStoriesDeleteLike({
    slug: activeVideoSlug ?? '',
    successCallback: () => {
      items[swiperIndex].likedByMe = false;
    },
  });

  const { trigger: triggerStoriesDislike } = useSwrStoriesDislike({
    slug: activeVideoSlug ?? '',
    successCallback: () => {
      items[swiperIndex].dislikedByMe = true;
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 4000);
    },
  });

  const { trigger: triggerDeleteStoriesDislike } = useSwrStoriesDeleteDislike({
    slug: activeVideoSlug ?? '',
    successCallback: () => {
      items[swiperIndex].dislikedByMe = false;
    },
  });

  const toggleMuteAll = useCallback((value?: string) => {
    if (!value) return;

    if (value === 'true') {
      setMuted(true);
    } else if (value === 'false') {
      setMuted(false);
    } else if (value === 'prev') {
      setMuted((prev) => !prev);
    }
  }, []);


  const { trigger: triggerUnlock } = useSwrResumeCheckUnlock({
    userSlug: items[swiperIndex]?.slug ?? '',
    type: 'contact',
    locale,
    successCallback: () => {
      setConfirmModal(false);
      setSelectedCvSlug(items[swiperIndex].slug ?? '');
    },
    errorCallback: (errors) => {
      const code = errors?.errors?.[0]?.code;
      setSelectedCvSlug(null);

      if (!code) {
        console.error('Unlock failed:', errors);
        return;
      }

      if (code === 403) {
        showAddCompanyInfoModal(locale);
      } else if (code === 601) {
        showUpgradeSubscriptionModal();
      } else {
        console.error('Unlock failed:', errors);
      }
    },
  });

  const updateVideos = (activeIndex: number) => {
    videoRefs.current.forEach((video, i) => {
      if (!video) return;
      if (i === activeIndex) {
        if (isAllowedPlayingVideo && !isLoadingSecurityCheck) {
          void video.play().catch(() => {});
        } else {
          video.pause();
          video.currentTime = 0;
        }
      }
    });
  };


  const handleStoriesStart = (index:number) => {
    const video = videoRefs.current[index];
    if (!video) return;
    video.play().catch(() => {});
    toggleMuteAll('false');
    setHasinteracted(true);
  };

  const handleVideoClick = useCallback(
    (index: number) => {
      if (!hasInteracted) return;

      const video = videoRefs.current[index];
      if (!video) return;

      if (video.paused) {
        video.play().catch(() => {});
        setIsPlaying(true);
        toggleMuteAll('false');
      } else {
        video.pause();
        setIsPlaying(false);
        toggleMuteAll('true');
      }

      // Show icon for 1 second
      setShowIcon(true);
      if (iconTimeout.current) clearTimeout(iconTimeout.current);
      iconTimeout.current = setTimeout(() => setShowIcon(false), 1000);
    },
    [hasInteracted, toggleMuteAll],
  );

  useEffect(() => {
    if (!activeVideoSlug) return;

    window.history.pushState(
      null,
      '',
      withLocale(`/stories?ps=${activeVideoSlug}`),
    );
  }, [activeVideoSlug, withLocale]);

  return (
    <div className="relative h-[calc(100dvh-100px-env(safe-area-inset-bottom))] md:container md:w-[500px] xl:w-[800px]">
      <ShareModal
        isOpen={isOpenShareModal}
        onClose={() => toggleShareModal(false)}
        ogTitle={t('meta.stories.share.modal.title')}
        ogDescription={t('meta.stories.share.modal.title')}
        pathnameShareUrl={`${getI18nLinkHref(`/stories?ps=${activeVideoSlug}`, locale)}`}
      />

      {selectedCvSlug && (
        <Modal
          isOpen
          onClose={() => {
            setSelectedCvSlug(null);
          }}
          size="full"
          className="!transform-none !overflow-visible"
          body={(
            <>
              <CvDetailHub
                trackingData={{
                  listType: 'stories',
                }}
                slug={selectedCvSlug}
                isAuthenticated
                closeModal={() => {
                  setSelectedCvSlug(null);
                }}
              />
            </>
          )}
        />
      )}

      {isAuth && (
        <Modal
          size="sm"
          isOpen={confirmModal}
          body={(
            <CustomConfirmModalContent
              userSlug={items[swiperIndex]?.slug ?? ''}
              onSendSuccess={(val: boolean) => {
                if (!val) {
                  setConfirmModal(false);
                  return;
                }
                if (val) {
                  void triggerUnlock({ jobId: 0, jobSlug: '', title: '' });
                }
              }}
            />
          )}
          onClose={() => null}
        />
      )}


      <Swiper
        direction="vertical"
        modules={[Navigation, Mousewheel, Keyboard]}
        keyboard={{ enabled: true, onlyInViewport: false }}
        mousewheel
        navigation={{ nextEl: '.next-btn', prevEl: '.prev-btn' }}
        slidesPerView={1}
        observeParents
        observer
        className="h-full"
        onSlideChange={(swiper) => {
          const index = swiper.activeIndex;
          setHasinteracted(true);
          setSwiperIndex(index);
          const activeItem = items[index];
          if (!activeItem) return;
          setActiveVideoSlug(activeItem.slug ?? '');
          updateVideos(swiper.activeIndex);
          trackEvent({
            eventType: 'view',
            listType: 'stories',
            entityType: 'resume',
            entityId: activeItem.slug ?? '',
            viewContext: 'detail',
            ...(userId && { userId: userId.toString() }),
            metadata: [
              { key: 'video', value: activeItem.slug ?? '' },
            ],
          });
          sendGoogleTagEvent('viewed_interview', 'Custom');

          // Fetch next page if near end
          if (!isValidating && size < totalPages && index >= items.length - 2) {
            void setSize(size + 1);
          }
        }}
      >
        {items.map((item, index) => {
          const firstVideo = item.videos?.[0];
          if (!firstVideo) return null;

          return (
            <SwiperSlide
              key={`${item.id}-${firstVideo.id}`} // index makes the key unique
              className="relative overflow-hidden !rounded-3xl border-y-8  border-surface-100"
            >

              {(isLoadingSecurityCheck || !isAllowedPlayingVideo)
                && (
                  <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-black opacity-100">
                    <div className="flex items-center space-x-3">
                      <div className="size-4 animate-bounce rounded-full bg-secondary" />
                      <div className="size-4 animate-bounce rounded-full bg-white [animation-delay:0.15s]" />
                      <div className="size-4 animate-bounce rounded-full bg-secondary [animation-delay:0.3s]" />
                    </div>

                    {/* We need server side check here */}
                    {!isAuth
                      && (
                      <LinkStyled
                        href="/login"
                        styling="solid"
                        size="sm"
                        color="secondary"
                        rounding="full"
                        className="mt-3"
                        aria-label={t('login.meta.title')}
                      >
                        {t('login.meta.title')}
                      </LinkStyled>
                      )}
                  </div>
                )}

              {/* Interaction buttons */}
              <div className="absolute right-4 top-8 z-2 flex flex-col items-center gap-5">

                {/* Mute / Unmute */}
                <StoriesActionIcons
                  onClick={() => toggleMuteAll('prev')}
                  icon={muted ? <SpeakerXMarkIcon className="size-5" /> : <SpeakerWaveIcon className="size-5" />}
                />

                {/* Share */}
                <StoriesActionIcons
                  onClick={() => toggleShareModal(true)}
                  icon={<ShareIcon className="size-5" />}
                />

                <StoriesActionIcons
                  onClick={!isAuth ? undefined : () => {
                    void (item.dislikedByMe ? triggerDeleteStoriesDislike() : triggerStoriesDislike());
                  }}
                  isLink={!isAuth}
                  href={!isAuth ? '/login' : undefined}
                  icon={item.dislikedByMe ? <FlagSolid className="size-5 text-ink" />
                    : <FlagOutline className="size-5 text-ink" />}
                />

                <StoriesActionIcons
                  onClick={!isAuth ? undefined : () => {
                    void (item.likedByMe ? triggerDeleteLike() : triggerLike());
                  }}
                  isLink={!isAuth}
                  href={!isAuth ? '/login' : undefined}
                  icon={item.likedByMe ? <StoriesUnClapIcon className="size-5" />
                    : <StoriesClapIcon className="size-5" />}
                  className={item.likedByMe ? '!bg-primary' : ''}
                />


                {/* Contact */}
                {isAuth ? (
                  <Button
                    type="button"
                    color="secondary"
                    rounding="full"
                    size="sm"
                    className="!size-10 !p-0"
                    onClick={() => {
                      const stored = sessionStorage.getItem('global.cv.detail.unblock');
                      if (stored === 'true') {
                        void triggerUnlock({ jobId: 0, jobSlug: '', title: '' });
                      } else {
                        setConfirmModal(true);
                      }
                    }}
                  >
                    <PhoneIcon className="size-5" />
                  </Button>
                ) : (
                  <LinkStyled href="/login" color="secondary" rounding="full" size="sm" className="!size-10 !p-0">
                    <PhoneIcon className="size-5" />
                  </LinkStyled>
                )}
              </div>


              <div
                role="presentation"
                className="absolute left-0 top-0 z-1 h-1 w-full cursor-pointer bg-gray-400"
                onClick={(e) => {
                  const activeVideo = videoRefs.current[swiperIndex];
                  if (!activeVideo) return;

                  const rect = (e.target as HTMLElement).getBoundingClientRect();
                  const clickX = e.clientX - rect.left;
                  const newTime = (clickX / rect.width) * activeVideo.duration;
                  activeVideo.currentTime = newTime;
                }}
              >
                <div
                  role="presentation"
                  className="relative h-1 w-full cursor-pointer bg-gray-400"
                  onClick={(e) => {
                    const video = videoRefs.current[swiperIndex];
                    if (!video) return;

                    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                    const clickX = e.clientX - rect.left;
                    video.currentTime = (clickX / rect.width) * video.duration;
                  }}
                >
                  <div
                    ref={(el) => {
                      if (el) progressRefs.current[index] = el;
                    }}
                    className="h-1 bg-secondary transition-all duration-200 ease-linear"
                  />
                </div>
              </div>
              <video
                ref={(el) => {
                  if (el) videoRefs.current[index] = el;
                }}
                src={Math.abs(index - swiperIndex) <= 1
                  ? firstVideo.url
                  : undefined}
                className="size-full bg-[#000000] object-cover md:object-contain"
                loop
                onClick={() => handleVideoClick(index)}
                muted={muted}
                onTimeUpdate={(e) => {
                  const video = e.currentTarget;
                  const progressEl = progressRefs.current[index];
                  if (!progressEl) return;
                  progressEl.style.width = `${(video.currentTime / video.duration) * 100}%`;
                }}
                playsInline
              />

              {!hasInteracted
                && (
                  <div
                    role="presentation"
                    onClick={() => handleStoriesStart(index)}
                    className="absolute inset-0 z-1 flex flex-col items-center justify-center bg-dark/30"
                  >
                    <div
                      className="flex size-16 items-center justify-center rounded-full bg-black/60"
                    >
                      <PlayIcon className="size-10 text-white opacity-90" />
                    </div>
                  </div>

                )}

              {/* Overlay Icon */}
              {showIcon && (
                <div
                  className="pointer-events-none absolute left-1/2 top-1/2 flex size-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-black/60"
                >
                  {isPlaying ? (
                    <PlayIcon className="size-10 animate-fade text-white opacity-90" />
                  ) : (
                    <PauseIcon className="size-10 animate-fade text-white opacity-90" />
                  )}
                </div>
              )}

              {showConfetti && (
                <Confetti />
              )}

              <div className="absolute inset-x-0 bottom-0 z-1 h-[200px] bg-story-bottom " />

              <div
                className="absolute top-1 flex h-[100px] w-full items-center justify-start bg-story-top  p-3 text-white"
              >

                <div className="flex items-center justify-start gap-2">
                  {item.imgUrl
                    && (
                      <div className="min-w-14">
                        <Avatar
                          size="sm"
                          imgPath={item.imgUrl}
                          aspectRatio="square-uncertain"
                          extractImageName
                          alt={item.slug}
                          className="mx-auto"
                        />
                      </div>
                    )}
                  {item.leadExperience
                    && (
                      <div className="pr-10 md:pr-0">
                        <div
                          className="max-w-[325px] overflow-hidden truncate whitespace-nowrap text-sm font-bold text-white md:text-lg"
                        >
                          {item.leadExperience?.title}
                        </div>
                        <div className="mb-2 text-xs text-white">
                          @
                          {item.leadExperience?.company}
                        </div>
                      </div>
                    )}
                </div>
              </div>


              <div
                className="absolute inset-x-0 bottom-0 z-1 flex flex-col items-center justify-between px-3 py-5 text-white md:bottom-0 md:flex-row"
              >
                <div
                  className="mb-4 text-center text-md font-bold md:text-left"
                >
                  {firstVideo.question}
                </div>

              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>
      {/* Custom navigation buttons */}
      <div className="absolute -right-16 top-1/2 z-2 -mt-12 hidden flex-col gap-4 md:flex">
        <button type="button" className="prev-btn rounded-full bg-surface p-2 text-ink">
          <ArrowUpIcon className="size-5" />
        </button>
        <button type="button" className="next-btn rounded-full bg-surface p-2 text-ink">
          <ArrowDownIcon className="size-5" />
        </button>
      </div>

    </div>
  );
}
